import { ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChefHat, RefreshCw, Trash2, Utensils } from 'lucide-react';
import { CostPriceIngredient } from '../../../../services/costPrice/costPriceService';
import { CustomIngredient } from '../../../../services/customIngredients/customIngredientsService';
import { Subrecipe } from '../../../../services/subrecipes/subrecipesService';
import { cn } from '../../../../lib/utils';
import { borderPrimary, primaryText, secondaryText, outlinedActionButtonClass, whiteBG } from '../../../../styling/stylingHelper';
import { SupplierLogo } from '../../../ui/SupplierLogo';
import { Platform } from '../../../../types/Platforms';
import { reviewCustomIngredient, rowKind } from './automationRunUtils';

type Props = {
  row: CostPriceIngredient; customs: CustomIngredient[]; restaurantId?: string; subrecipe?: Subrecipe; busy: boolean;
  sourceReadOnly?: boolean;
  onSwap?: () => void;
  currentPrice?: number;
  portionCost?: number;
  onPrice?: (price: number, custom: CustomIngredient) => Promise<void>;
  children?: ReactNode;
  onAmount: (amount: number) => Promise<void>;
  onName: (name: string, unit: string) => Promise<void>;
  onToggle: () => Promise<void>;
  onRemove: () => Promise<void>;
};
export default function AutomationIngredientRow({ row, customs, restaurantId, subrecipe, busy, sourceReadOnly = false, onSwap, currentPrice, portionCost, onPrice, children, onAmount, onName, onToggle, onRemove }: Props) {
  const { t, i18n } = useTranslation();
  const kind = rowKind(row, customs, restaurantId);
  const custom = reviewCustomIngredient(row, customs, restaurantId);
  const name = kind === 'subrecipe' ? subrecipe?.name ?? t('costPrice.automationRun.editor.missingSubrecipe') : row.productName ?? row.nestedCostPriceName ?? '';
  const unit = kind === 'subrecipe' ? subrecipe?.portionsUnit ?? 'p' : kind === 'nested' ? 'p' : row.quantityInfo?.stdUnit ?? 'p';
  const amount = kind === 'subrecipe' ? row.subrecipePortionsNeeded : kind === 'nested' ? row.nestedQuantityNeeded : row.quantityNeeded;
  const [nameDraft, setNameDraft] = useState(name);
  const [amountDraft, setAmountDraft] = useState(String(amount ?? ''));
  const [error, setError] = useState('');
  const [priceTarget, setPriceTarget] = useState<CustomIngredient | null>(null);
  const [priceDraft, setPriceDraft] = useState('');
  const [savingPrice, setSavingPrice] = useState(false);
  const priceUnit = custom?.quantityInfo.stdUnit ?? unit;
  const priceFactor = priceUnit === 'g' || priceUnit === 'ml' ? 1000 : 1;
  const priceUnitLabel = priceUnit === 'g' ? 'kg' : priceUnit === 'ml' ? 'L'
    : kind === 'subrecipe' || kind === 'nested' ? t('costPrice.automationRun.editor.portion')
    : ['p', 'piece', 'pc', 'pcs'].includes(priceUnit) ? t('costPrice.addIngredient.piece') : priceUnit;
  const priceLabel = t('costPrice.automationRun.editor.unitPriceValue', {
    price: currentPrice === undefined && custom?.price !== 0 ? '—' : new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'EUR' }).format((currentPrice ?? 0) * priceFactor),
    unit: priceUnitLabel,
  });
  const canPrice = !sourceReadOnly && !!onPrice && !!custom && ['g', 'ml', 'p'].includes(priceUnit);
  const saved = useRef({ name, amount });
  saved.current = { name, amount };
  useEffect(() => setNameDraft(name), [name]);
  useEffect(() => setAmountDraft(String(amount ?? '')), [amount]);
  const editableName = !sourceReadOnly && (kind === 'customUnpriced' || (kind === 'subrecipe' && !!subrecipe));
  const badgeKey = kind === 'subrecipe' ? 'subrecipeLabel' : kind === 'customUnpriced' || kind === 'customPriced' ? 'customLabel' : undefined;
  const toggle = kind === 'customUnpriced' || (kind === 'subrecipe' && !!subrecipe);
  const canSwap = !!onSwap && (kind === 'order' || kind === 'customUnpriced' || kind === 'customPriced');
  const act = async (work: () => Promise<void>) => {
    setError('');
    try { await work(); }
    catch (error) {
      console.error('Automation ingredient edit failed:', error);
      setError(t(`costPrice.automationRun.editor.${error instanceof Error && error.message === 'unitMismatch' ? 'unitMismatch' : error instanceof Error && error.name === 'SubrecipeDuplicateNameApiError' ? 'duplicateName' : 'saveFailed'}`));
      setNameDraft(saved.current.name); setAmountDraft(String(saved.current.amount ?? ''));
    }
  };
  const commitName = () => {
    const trimmed = nameDraft.trim();
    if (!trimmed) { setNameDraft(name); return; }
    if (trimmed !== name) void act(() => onName(trimmed, unit));
  };
  const commitAmount = () => {
    const parsed = Number(amountDraft.replace(',', '.'));
    if (!Number.isFinite(parsed) || parsed < 0) { setAmountDraft(String(amount ?? '')); return; }
    if (parsed !== amount) void act(() => onAmount(parsed));
  };
  const savePrice = async () => {
    const price = Number(priceDraft.replace(',', '.'));
    if (!Number.isFinite(price) || price <= 0) { setError(t('costPrice.automationRun.editor.invalidPrice')); return; }
    if (!priceTarget || !onPrice || savingPrice) return;
    setSavingPrice(true);
    await act(async () => { await onPrice(price, priceTarget); setPriceTarget(null); });
    setSavingPrice(false);
  };
  const input = cn('min-w-0 h-11 rounded-lg border px-3 text-sm disabled:opacity-70', borderPrimary, whiteBG, primaryText);
  return <div>
    <div className="grid grid-cols-[minmax(0,1fr)_44px] gap-2 lg:grid-cols-[minmax(0,1fr)_160px_120px_44px]">
      <div className="col-span-2 lg:col-span-1">
      <div className="relative">
      {editableName ? <input aria-label={t('costPrice.automationRun.editor.ingredient')} value={nameDraft} readOnly={!editableName} disabled={busy}
        className={cn(input, 'w-full', badgeKey && 'pr-28')} onChange={event => setNameDraft(event.target.value)} onBlur={commitName}
        onKeyDown={event => { if (event.key === 'Enter') event.currentTarget.blur(); }} /> :
        <div className={cn(input, 'flex items-center gap-2', badgeKey && 'pr-28')}>
          <span aria-label={t('costPrice.automationRun.editor.ingredient')} className="min-w-0 flex-1 truncate" title={name}>{name}</span>
          {kind === 'order' && row.platform && <SupplierLogo platform={row.platform.toLowerCase() as Platform} unknownSupplierName={row.customPlatformName}
            size="small" className="h-5 max-w-[64px] shrink-0" />}
        </div>}
        {badgeKey && <span className={cn('pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 whitespace-nowrap rounded px-2 py-1 text-[11px] font-medium',
          kind === 'subrecipe' ? 'bg-violet-50 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300')}>
          {t(`costPrice.automationRun.editor.${badgeKey}`)}
        </span>}
      </div>
      <div className={cn('mt-1 text-xs', secondaryText)}>
        {canPrice ? <button type="button" disabled={busy || savingPrice} className="min-h-[44px] text-left underline decoration-dotted underline-offset-4 lg:min-h-0"
          aria-label={t('costPrice.automationRun.editor.editUnitPrice', { name })}
          onClick={() => { setError(''); setPriceTarget(custom!); setPriceDraft(custom!.price === 0 ? '' : String(custom!.price / custom!.quantityInfo.stdAmount * priceFactor)); }}>
          {priceLabel}
        </button> : <span>{priceLabel}</span>}
      </div>
      </div>
      <div className="min-w-0">
      <div className="flex min-w-0">
        <input aria-label={t('costPrice.automationRun.editor.amount')} inputMode="decimal" value={amountDraft} disabled={busy}
          className={cn(input, 'w-full rounded-r-none')} onChange={event => setAmountDraft(event.target.value)} onBlur={commitAmount}
          onKeyDown={event => { if (event.key === 'Enter') event.currentTarget.blur(); }} />
        <select aria-label={t('costPrice.automationRun.editor.unit')} value={unit} disabled={busy || sourceReadOnly || kind !== 'customUnpriced'}
          className={cn(input, 'w-20 shrink-0 rounded-l-none border-l-0 px-2')}
          onChange={event => void act(() => onName(nameDraft.trim() || name, event.target.value))}>
          {Array.from(new Set(['g', 'ml', 'p', unit])).map(value => <option key={value} value={value}>{value === 'p' ? t('costPrice.automationRun.editor.portion') : value}</option>)}
        </select>
      </div>
      <p className={cn('mt-1 text-xs font-medium', primaryText)} aria-label={t('costPrice.automationRun.editor.portionCost')}>
        {t('costPrice.automationRun.editor.portionCost')}: {portionCost === undefined || !Number.isFinite(portionCost) ? '—' : new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'EUR' }).format(portionCost)}
      </p>
      </div>
      <div className={cn('col-span-2 row-start-3 flex flex-wrap items-center gap-1 lg:col-span-1 lg:row-start-auto', !toggle && !canSwap && 'hidden lg:flex')}>
        {canSwap && <button type="button" disabled={busy} onClick={onSwap}
          aria-label={t('costPrice.automationReview.swap')} title={t('costPrice.automationReview.swap')}
          className={cn(outlinedActionButtonClass, 'min-h-[44px] lg:min-h-0 disabled:opacity-40')}>
          <RefreshCw size={17} className="shrink-0" aria-hidden="true" />{t('costPrice.automationRun.editor.swapAction')}
        </button>}
        {toggle && <button type="button" disabled={busy} onClick={() => void act(onToggle)}
          aria-label={t(`costPrice.automationRun.editor.${kind === 'subrecipe' ? 'toIngredient' : 'toSubrecipe'}`)}
          title={t(`costPrice.automationRun.editor.${kind === 'subrecipe' ? 'toIngredient' : 'toSubrecipe'}`)}
          className={cn(outlinedActionButtonClass, 'min-h-[44px] lg:min-h-0 disabled:opacity-40')}>
          {kind === 'subrecipe' ? <Utensils size={17} className="shrink-0" aria-hidden="true" /> : <ChefHat size={17} className="shrink-0" aria-hidden="true" />}
          {t(`costPrice.automationRun.editor.${kind === 'subrecipe' ? 'ingredient' : 'recipeAction'}`)}
        </button>}
      </div>
      <button type="button" aria-label={t('common.delete')} disabled={busy} onClick={() => void act(onRemove)}
        className="col-start-2 row-start-2 flex h-11 items-center justify-center rounded-lg lg:col-start-auto lg:row-start-auto text-red-500 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40"><Trash2 size={17} /></button>
    </div>
    {priceTarget && <form className={cn('mt-2 rounded-lg border p-3 text-sm', borderPrimary, whiteBG)} onSubmit={event => { event.preventDefault(); void savePrice(); }}>
      <label className="flex flex-col gap-2">
        <span>{t('costPrice.automationRun.editor.pricePerUnitExVat', { unit: priceTarget.quantityInfo.stdUnit === 'g' ? 'kg' : priceTarget.quantityInfo.stdUnit === 'ml' ? 'L' : t('costPrice.addIngredient.piece') })}</span>
        <input aria-label={t('costPrice.automationRun.editor.unitPrice')} autoFocus inputMode="decimal" value={priceDraft} disabled={savingPrice || busy}
          className={input} onChange={event => setPriceDraft(event.target.value)} />
      </label>
      <p className={cn('mt-2 text-xs', secondaryText)}>{t('costPrice.automationRun.editor.sharedPriceHelp')}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="submit" disabled={savingPrice || busy} className={cn(outlinedActionButtonClass, 'min-h-[44px]')}>{t('common.save')}</button>
        <button type="button" disabled={savingPrice} className={cn(outlinedActionButtonClass, 'min-h-[44px]')} onClick={() => setPriceTarget(null)}>{t('common.cancel')}</button>
      </div>
    </form>}
    {children}
    {error && <p role="alert" className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>}
  </div>;
}
